import React, { ReactNode } from 'react';
import { ReportExportColumn } from '@core/utils/methods/report-export-table-data';
import {
  DatesAtAGlanceRow,
  OptionProps,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import { DATES_AT_A_GLANCE_MILESTONE_STATUS_LABELS } from '../dates-at-a-glance.constants';
import {
  getDatesAtAGlanceMilestoneStatus,
  getDatesAtAGlanceMilestoneStatusClassName,
} from '../dates-at-a-glance-helpers';
import {
  getDatesAtAGlanceSortableColumn,
  renderDateValue,
  renderDisplayValue,
  renderValue,
} from './common';

const renderDatesStatus = (
  _value: unknown,
  record: DatesAtAGlanceRow,
): ReactNode => {
  const status = getDatesAtAGlanceMilestoneStatus(record);

  return React.createElement(
    'span',
    { className: getDatesAtAGlanceMilestoneStatusClassName(status) },
    DATES_AT_A_GLANCE_MILESTONE_STATUS_LABELS[status],
  );
};

export const getDatesAtAGlanceColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ReportExportColumn<DatesAtAGlanceRow>[] => [
  {
    key: 'milestoneStatus',
    exportValue: record =>
      DATES_AT_A_GLANCE_MILESTONE_STATUS_LABELS[
        getDatesAtAGlanceMilestoneStatus(record)
      ],
    render: renderDatesStatus,
    title: 'Milestone Status',
  },
  getDatesAtAGlanceSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'oracleProjectNumbersDisplay',
    key: 'oracleProjectNumbersDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.oracleProjectNumbersDisplay),
    title: 'Oracle Project Numbers',
  },
  {
    dataIndex: 'iwmJobNumbersDisplay',
    key: 'iwmJobNumbersDisplay',
    title: 'IWM Job Numbers',
  },
  {
    dataIndex: 'paceProjectNumbersDisplay',
    key: 'paceProjectNumbersDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.paceProjectNumbersDisplay),
    title: 'PACE Project Numbers',
  },
  getDatesAtAGlanceSortableColumn(
    'taskName',
    'Milestone / Task Name',
    ReportsSortBy.TaskName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'paceTaskID',
    key: 'paceTaskID',
    render: renderValue,
    title: 'PACE Task ID',
  },
  {
    dataIndex: 'paceTaskScreensDisplay',
    key: 'paceTaskScreensDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.paceTaskScreensDisplay, record.paceTaskScreens),
    title: 'PACE Task Module(s)',
  },
  getDatesAtAGlanceSortableColumn(
    'projectType',
    'Project Type',
    ReportsSortBy.ProjectType,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'projectStatus',
    'Project Status',
    ReportsSortBy.ProjectStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'progressStatus',
    'Progress Status',
    ReportsSortBy.ProgressStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'siteNumber',
    'Site Number',
    ReportsSortBy.SiteNumber,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'FALocationNumber',
    key: 'FALocationNumber',
    render: renderValue,
    title: 'FA Location Number',
  },
  {
    dataIndex: 'state',
    key: 'state',
    render: renderValue,
    title: 'State',
  },
  {
    dataIndex: 'submarket',
    key: 'submarket',
    render: renderValue,
    title: 'Submarket',
  },
  {
    dataIndex: 'towerOwner',
    key: 'towerOwner',
    render: renderValue,
    title: 'Tower Owner',
  },
  getDatesAtAGlanceSortableColumn(
    'forecastedDate',
    'Forecasted Date',
    ReportsSortBy.ForecastDate,
    filters,
    sortByOptions,
    renderDateValue,
  ),
  {
    dataIndex: 'PACEForecastedDate',
    key: 'PACEForecastedDate',
    render: renderDateValue,
    title: 'PACE Forecasted Date',
  },
  getDatesAtAGlanceSortableColumn(
    'actualDate',
    'Actual Date',
    ReportsSortBy.ActualDate,
    filters,
    sortByOptions,
    renderDateValue,
  ),
  {
    dataIndex: 'PACEActualDate',
    key: 'PACEActualDate',
    render: renderDateValue,
    title: 'PACE Actual Date',
  },
];

export const getDatesAtAGlanceReportRowClassName = (
  record: DatesAtAGlanceRow,
): string =>
  [
    record.projectBundleID
      ? 'prov-dates-at-a-glance-report__row--clickable'
      : 'prov-dates-at-a-glance-report__row--disabled',
  ]
    .filter(Boolean)
    .join(' ');

export const getDatesAtAGlanceReportRowKey = (
  record: DatesAtAGlanceRow,
): string => record.projectPaceTaskDataId;
